import { Locale } from '@core/constants/locales.constants';
import { Fixture, Season } from '@graphql/index';
import { Translations } from '@lib/dictionary-keys/__generated__';
import {
  CleanContentful,
  CleanedContentfulImageProps,
} from '@stadionhq-v3/clean-contentful';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { FormGuideRequestProps } from '@stadionhq-v3/football-ui';
import { Club } from '@stadionhq-v3/football-ui/lib/types/club.types';
import { Competition } from '@stadionhq-v3/football-ui/lib/types/competition.types';
import dayjs from '@lib/dayjs';
import { SponsorCardImagePosition } from '@stadionhq-v3/base-ui';

export type FormGuideTransformTranslations = {
  noContent: {
    label: Translations['matchCentre']['buildUp']['noContentTitle'];
    text: Translations['matchCentre']['buildUp']['noContentText'];
  };
  error: {
    label: Translations['matchCentre']['buildUp']['noContentTitle'];
    text: Translations['matchCentre']['buildUp']['noContentText'];
  };
  loading: Translations['matchCentre']['buildUp']['loading'];
  formGuideLabels: {
    formLabel: Translations['matchCentre']['label']['form'];
    winAbbreviated: Translations['standings']['formIndicator']['winAbbreviated'];
    drawAbbreviated: Translations['standings']['formIndicator']['drawAbbreviated'];
    lossAbbreviated: Translations['standings']['formIndicator']['lossAbbreviated'];
    positionHeading: Translations['standings']['tableHeading']['position'];
    pointsHeading: Translations['standings']['tableHeading']['points'];
    playedHeading: Translations['standings']['tableHeading']['played'];
  };
  sponsoredBy: Translations['common']['label']['sponsoredBy'];
};

const formGuideTransform = ({
  title,
  sponsor,
  competition,
  opposition,
  club,
  fixtureData,
  currentSeason,
  prevSeason,
  dictionary,
  locale,
}: {
  title?: string;
  sponsor?: CleanedContentfulImageProps;
  competition: Competition;
  opposition: Club;
  club: Club;
  fixtureData: CleanContentful<Fixture> | undefined;
  currentSeason?: CleanContentful<Season>;
  prevSeason?: CleanContentful<Season>;
  dictionary: FormGuideTransformTranslations;
  locale: Locale;
}): Omit<FormGuideRequestProps, 'SPORTS_DATA_API'> => {
  const djs = dayjs(locale);
  const kickOffUtc = fixtureData
    ? djs(fixtureData.kickOffUtc).format('YYYY-MM-DD')
    : '';

  return {
    title: title || undefined,
    sponsor: sponsor
      ? {
          image: <ContentfulImage {...sponsor} />,
          title: dictionary.sponsoredBy,
          imagePosition: 'right' as SponsorCardImagePosition,
          onDark: false,
        }
      : undefined,
    onDark: false,
    dictionary,
    competition,
    oppositionCrest: opposition.crestImageUrl ? (
      <ContentfulImage
        url={opposition.crestImageUrl}
        alt={opposition.name ?? ''}
        height={76}
        width={76}
      />
    ) : null,
    oppositionName: opposition.name ?? '',
    opposition,
    teamCrest: club.crestImageUrl ? (
      <ContentfulImage
        url={club.crestImageUrl}
        alt={club.name ?? ''}
        height={76}
        width={76}
      />
    ) : null,
    teamName: club.name ?? '',
    team: club,
    toDate: kickOffUtc.toString() ?? '',
    showLeagueStats: fixtureData?.competition?.hasLeagueTable,
    currentSeasonId: currentSeason?.dataProviderId ?? '',
    prevSeasonId: prevSeason?.dataProviderId ?? '',
  };
};

export default formGuideTransform;
